// Hub de administração — porta de entrada (essencial no mobile) com as ferramentas
// liberadas para o perfil do usuário + status/disparo da sincronização.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, RefreshCw, ShieldCheck, Timer, Users, Grid3x3 } from 'lucide-react';
import { dispararSync, getSyncStatus, SyncRun } from '../api';
import { getUser } from '../auth';
import { Perfil } from '../types';
import { fmtDataHoraFoto } from '../utils/format';

interface Ferramenta {
  titulo: string;
  desc: string;
  rota: string;
  icone: typeof Map;
  perfis: Perfil[];
}

const FERRAMENTAS: Ferramenta[] = [
  { titulo: 'SLA por Seller', desc: 'Prazo de envio em dias, aplicado a todas as regiões de uma vez', rota: '/service-level/admin/sla-sellers', icone: Timer, perfis: ['ADMIN'] },
  { titulo: 'Regiões & SLAs', desc: 'Matriz seller × região para ajuste fino (dias corridos/úteis)', rota: '/service-level/admin/regioes-slas', icone: Grid3x3, perfis: ['ADMIN'] },
];

export default function AdminHub() {
  const user = getUser();
  const [status, setStatus] = useState<{ ts_foto: string | null; runs: SyncRun[] } | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => getSyncStatus().then(setStatus).catch(() => {});
  useEffect(() => { load(); }, []);

  const ferramentas = FERRAMENTAS.filter((f) => user && f.perfis.includes(user.perfil));
  const ultima = status?.runs?.[0];
  const rodando = ultima?.status === 'RUNNING' || sincronizando;

  const sincronizar = async () => {
    setSincronizando(true);
    setMsg('Sincronização disparada — os dados continuam disponíveis durante a carga (~9 min).');
    try {
      const r = await dispararSync();
      setMsg(r.ok ? `Sincronização concluída: ${r.detalhe ?? 'ok'}` : `Falha: ${r.erro}`);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Falha ao sincronizar.');
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="flex items-center gap-2"><ShieldCheck size={22} /> Administração</h1>
          <div className="sub">Ferramentas liberadas para o seu perfil ({user?.perfil})</div>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(260px,1fr))] mb-4">
        {ferramentas.map((f) => {
          const Icone = f.icone;
          return (
            <Link key={f.rota} to={f.rota} className="card !mb-0 no-underline text-inherit hover:border-accent transition-colors">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-[#eef4fc] text-accent flex items-center justify-center shrink-0">
                  <Icone size={20} />
                </span>
                <div>
                  <strong className="block text-sm">{f.titulo}</strong>
                  <span className="text-xs text-muted">{f.desc}</span>
                </div>
              </div>
            </Link>
          );
        })}
        {ferramentas.length === 0 && (
          <div className="card !mb-0"><p className="empty">Seu perfil não tem ferramentas administrativas.</p></div>
        )}
      </div>

      {user?.perfil === 'ADMIN' && (
        <div className="card">
          <h2 className="flex items-center gap-2"><RefreshCw size={14} /> Sincronização com o Databricks</h2>
          <div className="pedido-header mb-3">
            <div className="field"><div className="fl">Última atualização</div>
              <div className="fv">{fmtDataHoraFoto(status?.ts_foto)}</div></div>
            <div className="field"><div className="fl">Última execução</div>
              <div className="fv">
                {ultima
                  ? <span className={`badge ${ultima.status === 'OK' ? 'FINALIZADO' : ultima.status === 'RUNNING' ? 'ENVIADO' : 'p3'}`}>{ultima.status}</span>
                  : '—'}
              </div></div>
            <div className="field"><div className="fl">Detalhe</div>
              <div className="fv text-xs font-normal">{ultima?.detalhe ?? '—'}</div></div>
          </div>
          <button className="btn" onClick={sincronizar} disabled={rodando}>
            <RefreshCw size={14} className={rodando ? 'animate-spin' : ''} />
            {rodando ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
          {msg && <p className="chart-note mt-2">{msg}</p>}
        </div>
      )}
    </>
  );
}
